/**
 * Typed predicate grammar - mirrors `apps/rules/predicates.py`.
 *
 * Keep the shape in lockstep with the backend. The names here match the
 * Python dataclasses so JSON round-trips without translation.
 */

export type Op =
  | 'eq'
  | 'ne'
  | 'in'
  | 'not_in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'exists'
  | 'matches'
  | 'not_matches'
  | 'already_exists'
  | 'count_eq'
  | 'count_ne'
  | 'count_gt'
  | 'count_gte'
  | 'count_lt'
  | 'count_lte'
  | 'count_between'

export const VALUELESS_OPS: ReadonlySet<Op> = new Set(['exists', 'already_exists'])

/**
 * Ops whose ``value`` is the *count* of items in a list-shaped resolved
 * value (e.g. MULTI_SELECT registration field). The value input renders
 * a number, not a literal of the field's element type.
 */
export const COUNT_OPS: ReadonlySet<Op> = new Set([
  'count_eq',
  'count_ne',
  'count_gt',
  'count_gte',
  'count_lt',
  'count_lte',
  'count_between',
])

/**
 * Inferred type of a ref - drives which op set and value input render.
 *
 * ``multi_enum`` is the multi-select counterpart of ``enum``: the value
 * stored on the registration is a ``string[]`` rather than a single
 * string, so the op picker leans on count-based comparisons and the
 * value picker offers multi-select widgets where appropriate.
 */
export type RefType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'enum'
  | 'multi_enum'

/** Category grouping used in the ref picker Select. */
export type RefCategory =
  | 'registration_field'
  | 'custom_registration_field'
  | 'entity'
  | 'warranty_field'
  | 'aggregate'
  | 'role'
  | 'permission'
  | 'computed'
  | 'other'

export interface AvailableRef {
  /** Raw ref string, e.g. `field:consumer_email` or `entity:host.installed_at`. */
  ref: string
  /** Display label shown in the ref picker. */
  label: string
  /** Category used to group options in the Select. */
  category: RefCategory
  /** Inferred type - drives op picker and value input. */
  type: RefType
  /** Enum options when `type === 'enum'`. */
  options?: { value: string; label: string }[]
}

export interface ClauseNode {
  type: 'clause'
  ref: string
  op: Op
  value?: unknown
}

export interface GroupNode {
  type: 'group'
  operator: 'and' | 'or'
  children: PredicateNode[]
}

export type PredicateNode = ClauseNode | GroupNode

/** Builder state = top-level AND group of clauses (AND-only for now). */
export interface BuilderState {
  clauses: ClauseNode[]
}

const EMPTY_CLAUSE: ClauseNode = { type: 'clause', ref: '', op: 'eq', value: '' }

export function emptyClause(): ClauseNode {
  return { ...EMPTY_CLAUSE }
}

export function emptyBuilderState(): BuilderState {
  return { clauses: [emptyClause()] }
}

/** Hydrate a predicate JSON tree into the flat builder state.
 *
 *  Only AND groups of clauses are supported - anything else (nested
 *  groups, OR combinators) is rejected and the builder falls back to an
 *  empty state. That keeps S4/S5 free to extend the grammar without
 *  silently mishandling legacy data.
 */
export function predicateToBuilder(predicate: unknown): BuilderState {
  if (!predicate || typeof predicate !== 'object') return emptyBuilderState()
  const node = predicate as PredicateNode
  if (node.type === 'clause') return { clauses: [node] }
  if (node.type === 'group' && node.operator === 'and') {
    const clauses = node.children.filter(
      (c): c is ClauseNode => c.type === 'clause',
    )
    return clauses.length ? { clauses } : emptyBuilderState()
  }
  return emptyBuilderState()
}

/** A value-requiring op has a usable value: a non-blank scalar, or a non-empty
 *  array whose elements are each usable (covers in / between / count_*). 0 and
 *  false are real values; '' / null / undefined / [] are not. */
function clauseHasValue(value: unknown): boolean {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0 && value.every(clauseHasValue)
  return true
}

/** Complete enough to persist? Drops half-authored rows (a field + op chosen
 *  but the value left blank) so they are never emitted as `op ''`. Valueless
 *  ops (exists / already_exists) need no value. */
function isCompleteClause(c: ClauseNode): boolean {
  if (!c.ref || !c.op) return false
  return VALUELESS_OPS.has(c.op) || clauseHasValue(c.value)
}

/** Serialise builder state back to the Predicate JSON shape. */
export function builderToPredicate(state: BuilderState): PredicateNode | null {
  const valid = state.clauses.filter(isCompleteClause)
  if (valid.length === 0) return null
  if (valid.length === 1) return valid[0]
  return { type: 'group', operator: 'and', children: valid }
}

/** Ops allowed for a given ref type - filters the op Select. */
export function opsForType(type: RefType): Op[] {
  switch (type) {
    case 'string':
      return [
        'eq',
        'ne',
        'in',
        'not_in',
        'exists',
        'matches',
        'not_matches',
        'already_exists',
      ]
    case 'number':
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'exists', 'already_exists']
    case 'date':
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'exists', 'already_exists']
    case 'boolean':
      return ['eq', 'ne', 'exists']
    case 'enum':
      return ['eq', 'ne', 'in', 'not_in', 'exists', 'already_exists']
    case 'multi_enum':
      return [
        'in',
        'not_in',
        'count_eq',
        'count_ne',
        'count_gt',
        'count_gte',
        'count_lt',
        'count_lte',
        'count_between',
        'exists',
      ]
  }
}

export const OP_LABELS: Record<Op, string> = {
  eq: 'equals',
  ne: 'does not equal',
  in: 'is one of',
  not_in: 'is not one of',
  gt: 'greater than',
  gte: 'greater or equal',
  lt: 'less than',
  lte: 'less or equal',
  between: 'between',
  exists: 'is provided',
  matches: 'matches pattern',
  not_matches: 'does not match pattern',
  already_exists: 'already exists',
  count_eq: 'has exactly',
  count_ne: 'does not have exactly',
  count_gt: 'has more than',
  count_gte: 'has at least',
  count_lt: 'has fewer than',
  count_lte: 'has at most',
  count_between: 'has between',
}

export const CATEGORY_LABELS: Record<RefCategory, string> = {
  registration_field: 'Registration fields',
  custom_registration_field: 'Custom registration fields',
  entity: 'Entities',
  warranty_field: 'Warranty fields',
  aggregate: 'Aggregates',
  role: 'Roles',
  permission: 'Permissions',
  computed: 'Computed values',
  other: 'Other',
}
